import DownloadIcon from "@ch/assets/icons/pack/DownloadIcon.tsx";
import Eye2Open from "@ch/assets/icons/pack/Eye2Open.tsx";
import EmptyStateComponent from "@ch/components/EmptyState";
import PageHeader from "@ch/components/Header/PageHeader";
import Icon from "@ch/components/Icons/Icon";
import ComponentLoader from "@ch/components/Loaders/ComponentLoader";
import ConfirmDeleteModal from "@ch/components/dialogs/ConfirmDeleteModal";
import type {
  PrescriptionWithRelations,
  UsersWithRelations,
} from "@ch/generated";
import useDialogue from "@ch/helpers/hooks/useDialogue";
import { NotifyError } from "@ch/helpers/utils/helpers";
import AddNewCard from "@ch/pages/HealthPassportPage/components/AddNewCard.tsx";
import SingleHealthPassportTopCard from "@ch/pages/HealthPassportPage/components/SingleHealthPassportTopCard";
import SingleHistoryMetricCard from "@ch/pages/HealthPassportPage/components/SingleHistoryMetricCards";
import useDeleteMedicationPassport from "@ch/pages/HealthPassportPage/hooks/medication/useDeleteMedication";
import useGetPrescriptionById from "@ch/pages/HealthPassportPage/hooks/prescription/useGetPrescriptionById";
import useGetFrequencies from "@ch/pages/HealthPassportPage/hooks/useGetFrequencies";
import useGetMedications from "@ch/pages/HealthPassportPage/hooks/useGetMedications";
import AddPrescription from "@ch/pages/HealthPassportPage/modals/AddPrescription";
import { useAppSelector } from "@ch/redux-store/hooks/useAppSelector.ts";
import { preparePrescriptionTemplate } from "@ch/templates";
import moment from "moment";
import { useState } from "react";
import { useParams } from "react-router-dom";

const PrescriptionDetailsPage = () => {
  const { id } = useParams();
  const user = useAppSelector((state) => state.user?.currentUser);

  const {
    loading,
    prescriptionsPassport,
    refetch: refetchPrescription,
  } = useGetPrescriptionById(id);
  const [activeData, setActiveData] =
    useState<PrescriptionWithRelations | null>(null);
  const [activeMedicationPassportId, setActiveMedicationPassportId] =
    useState("");
  const [pdfProcessing, setPdfProcessing] = useState<boolean>(false);

  const { frequencies } = useGetFrequencies();
  const { medications } = useGetMedications();
  const {
    dialogueId: deleteId,
    handleOpenDialogue: handleOpenMedicationDelete,
    handleCloseDialogue: handleCloseMedicationDelete,
  } = useDialogue();
  const { deleteMedicationPassport, loading: deleting } =
    useDeleteMedicationPassport({
      id: activeData?.id ?? "",
      callback: () => {
        if (refetchPrescription) {
          refetchPrescription();
        }
        handleCloseMedicationDelete();
      },
    });
  const {
    dialogueId: medicationData,
    handleOpenDialogue,
    handleCloseDialogue,
    handleUnmountDialogue,
    isDialogueOpen,
  } = useDialogue();

  const {
    dialogueId: medicationDataAdd,
    handleOpenDialogue: handleOpenDialogueAdd,
    handleCloseDialogue: handleCloseDialogueAdd,
    isDialogueOpen: isDialogueOpenAdd,
  } = useDialogue();

  const handlePdf = async (action?: string) => {
    const html = preparePrescriptionTemplate(
      prescriptionsPassport!,
      user as UsersWithRelations & {
        facilityName: string;
        facilityLocation: string;
      },
    );

    const opt = {
      margin: 1,
      filename: `${prescriptionsPassport?.user?.name} Prescription ${
        prescriptionsPassport?.title ?? ""
      }.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: {
        unit: "mm",
        format: "a4",
        orientation: "portrait",
      },
    };

    try {
      setPdfProcessing(true);
      if (action === "view") {
        await html2pdf()
          .from?.(html)
          .set(opt)
          .toPdf()
          .output("blob")
          .then((pdf: Blob) => {
            window.open(URL.createObjectURL(pdf), "_blank");
          });
      } else {
        await html2pdf?.().from?.(html)?.set?.(opt)?.save();
      }
    } catch {
      NotifyError("Error processing pdf");
    } finally {
      setPdfProcessing(false);
    }
  };
  return (
    <>
      <PageHeader />
      <div className="grid gap-5">
        <SingleHealthPassportTopCard
          bgColor="bg-orange"
          iconBg={<Icon name="pill" size={150} />}
          title="Prescription Details"
        />
        <div className="rounded-xl bg-white p-4 lg:p-8">
          {loading ? (
            <ComponentLoader />
          ) : prescriptionsPassport?.medicationPassports?.length === 0 ? (
            <div className="mx-auto flex h-4/6 w-full items-center justify-center">
              <EmptyStateComponent
                info="You have not added any records yet."
                title="No Prescription Records!"
              />
            </div>
          ) : (
            <>
              <div>
                <div className="mb-4 text-[32px] font-bold">
                  {prescriptionsPassport?.title}
                </div>
                <div className="mb-4 flex grow justify-end gap-2">
                  <button
                    className="text-slate-500 flex items-center hover:bg-gray-100"
                    disabled={pdfProcessing}
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    onClick={() => handlePdf("view")}
                    type="button"
                  >
                    <Eye2Open />
                    <span className="font-normal">View PDF</span>
                  </button>
                  <button
                    className="text-slate-500 flex items-center gap-1 p-2 text-sm hover:bg-gray-100"
                    disabled={pdfProcessing}
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    onClick={() => handlePdf()}
                    type="button"
                  >
                    <DownloadIcon />
                    <span className="font-normal">Download PDF</span>
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                {prescriptionsPassport?.medicationPassports?.map(
                  (medicationPassport) => (
                    <SingleHistoryMetricCard
                      data={{
                        medication:
                          medicationPassport.medicine?.name ?? "--/--",
                        source: medicationPassport?.source ?? "--/--",
                        date:
                          moment(medicationPassport?.startDate).format(
                            "YYYY/MM/DD",
                          ) ?? "",
                      }}
                      key={medicationPassport?.id}
                      labels={{
                        medication: "Name",
                        date: "Start Date",
                        source: "Prescribed by",
                      }}
                      onDelete={() => {
                        void setActiveData(medicationPassport);
                        void handleOpenMedicationDelete();
                      }}
                      onEdit={() => {
                        setActiveMedicationPassportId(medicationPassport.id!);
                        handleOpenDialogue();
                      }}
                    />
                  ),
                )}
                <AddNewCard onAdd={handleOpenDialogueAdd}>
                  Add medication
                </AddNewCard>
              </div>
            </>
          )}
        </div>
        {isDialogueOpen && (
          <AddPrescription
            addSaveAndAdd={false}
            editExistingMedication={isDialogueOpen}
            editMedicationPassportId={activeMedicationPassportId}
            frequencies={frequencies}
            isOpen={isDialogueOpen}
            medications={medications}
            modalId={medicationData}
            onClose={() => {
              handleCloseDialogue();
              setActiveData(null);
            }}
            prescription={prescriptionsPassport}
            refetchPrescription={refetchPrescription}
            unmountDialogue={handleUnmountDialogue}
          />
        )}

        {isDialogueOpenAdd && (
          <AddPrescription
            addSaveAndAdd={false}
            editExistingMedication={false}
            editMedicationPassportId={activeMedicationPassportId}
            frequencies={frequencies}
            isOpen={isDialogueOpenAdd}
            medications={medications}
            modalId={medicationDataAdd}
            onClose={() => {
              handleCloseDialogueAdd();
              setActiveData(null);
            }}
            prescription={prescriptionsPassport}
            refetchPrescription={refetchPrescription}
            unmountDialogue={handleUnmountDialogue}
          />
        )}
        <ConfirmDeleteModal
          confirmDelete={() => {
            deleteMedicationPassport({ id: activeData?.id ?? "" });
          }}
          handleClose={() => {
            handleCloseMedicationDelete();
            setActiveData(null);
          }}
          isDeleteLoading={deleting}
          modalId={deleteId}
        />
      </div>
    </>
  );
};

export default PrescriptionDetailsPage;
